interface HoneybadgerNotice {
  name?: string
  message?: string
  url?: string
  context?: Record<string, unknown>
  backtrace?: { file?: string; method?: string }[]
  tags?: string | string[]
  fingerprint?: string
}

interface NoiseClassifier {
  tag: string
  matches: (notice: HoneybadgerNotice) => boolean
}

const THIRD_PARTY_ORIGINS: string[] = [
  'googletagmanager.com',
  'google-analytics.com',
  'js.stripe.com',
  'static.hotjar.com',
  'script.hotjar.com'
]

const BROWSER_EXTENSION_PREFIXES: string[] = [
  'user-script:',
  'chrome-extension://',
  'moz-extension://',
  'safari-web-extension://',
  'webkit-masked-url://'
]

// Messages that only the WebExtensions API produces. Our code never calls this
// API, so a notice with one of these messages comes from an extension.
const EXTENSION_API_MESSAGES: string[] = [
  'Invalid call to runtime.',
  'runtime.sendMessage',
  'Extension context invalidated',
  'Could not establish connection. Receiving end does not exist'
]

const TRANSPORT_MESSAGES: string[] = ['Load failed', 'Failed to fetch']

const TELEMETRY_URL_MARKERS: string[] = [
  '/logger/log',
  '/version.txt',
  'hotjar.com',
  'googletagmanager.com'
]

const STORAGE_MARKERS: string[] = [
  'SecurityError',
  'QuotaExceededError',
  'localStorage',
  'The operation is insecure'
]

export const THIRD_PARTY_TAG = 'third-party'
export const BROWSER_EXTENSION_TAG = 'browser-extension'
export const TELEMETRY_TRANSPORT_TAG = 'telemetry-transport'
export const STORAGE_BLOCKED_TAG = 'storage-blocked'
export const NOISE_TAG = 'noise'

/**
 * Determines whether a notice is pure third-party script noise: every
 * identifiable frame comes from a known third-party origin, and no frame comes
 * from our own bundle. Neutral frames (`<anonymous>`, `<unknown>`, empty) are
 * ignored so a GTM stack that starts with `<anonymous>:1` still classifies.
 * @param notice - The Honeybadger notice being evaluated in `beforeNotify`.
 * @returns true when the error originates entirely from third-party scripts.
 */
export function isThirdParty(notice: HoneybadgerNotice): boolean {
  const frames = notice.backtrace ?? []
  if (frames.length === 0) return false

  let hasThirdParty = false

  for (const frame of frames) {
    const file = (frame.file || '').trim()
    if (['', '<anonymous>', '<unknown>'].includes(file)) continue

    if (THIRD_PARTY_ORIGINS.some((origin) => file.includes(origin))) {
      hasThirdParty = true
      continue
    }

    // A frame from our own bundle disqualifies the whole notice.
    return false
  }

  return hasThirdParty
}

/**
 * Returns the notice message plus `context.errorMessage`, which is where the
 * unhandled-rejection handler records the rejection reason's message.
 * @param notice - The Honeybadger notice being evaluated.
 * @returns The non-empty messages on the notice.
 */
function noticeMessages(notice: HoneybadgerNotice): string[] {
  return [notice.message, notice.context?.errorMessage].filter(
    (value): value is string => typeof value === 'string' && value.length > 0
  )
}

/**
 * Determines whether a notice comes from browser-extension code.
 *
 * Frames: unlike {@link isThirdParty}, a single extension frame is enough:
 * extensions monkey-patch globals, so our own frames are usually on the stack
 * too. `user-script` and `webkit-masked-url://` are WebKit's labels for Safari
 * extension content scripts.
 *
 * Messages: some extension rejections reach us with no stack. The reason comes
 * from the extension's isolated world, so it is not an `Error` in our realm,
 * and the only backtrace is our own reporter. For those, the message is the
 * only signal: it names a WebExtensions API that our code never calls
 * (for example Safari's "Invalid call to runtime.sendMessage(). Tab not found.").
 * @param notice - The Honeybadger notice being evaluated in `beforeNotify`.
 * @returns true when a frame or the message comes from a browser extension.
 */
export function isBrowserExtension(notice: HoneybadgerNotice): boolean {
  const hasExtensionFrame = (notice.backtrace ?? []).some((frame) => {
    const file = (frame.file || '').trim()
    return (
      file === 'user-script' || BROWSER_EXTENSION_PREFIXES.some((prefix) => file.startsWith(prefix))
    )
  })
  if (hasExtensionFrame) return true

  return noticeMessages(notice).some((message) =>
    EXTENSION_API_MESSAGES.some((marker) => message.includes(marker))
  )
}

function noticeUrls(notice: HoneybadgerNotice): string[] {
  const context = notice.context ?? {}
  const candidates: unknown[] = [
    notice.url,
    notice.message,
    context.currentUrl,
    context.requestUrl,
    context.url,
    ...(notice.backtrace ?? []).flatMap((frame) => [frame.file, frame.method])
  ]

  return candidates.filter(
    (value): value is string => typeof value === 'string' && value.length > 0
  )
}

/**
 * Determines whether a notice is a failed telemetry request (our own logger or
 * version check, Hotjar, or GTM) rather than an application error.
 * @param notice - The Honeybadger notice being evaluated in `beforeNotify`.
 * @returns true for a `TypeError` transport failure aimed at a telemetry URL.
 */
export function isTelemetryTransport(notice: HoneybadgerNotice): boolean {
  if (notice.name !== 'TypeError') return false

  const message = notice.message ?? ''
  if (!TRANSPORT_MESSAGES.some((transportMessage) => message.includes(transportMessage)))
    return false

  return noticeUrls(notice).some((url) =>
    TELEMETRY_URL_MARKERS.some((marker) => url.includes(marker))
  )
}

/**
 * Determines whether a notice comes from a browser blocking web storage
 * (private mode, storage quota, or cross-site restrictions).
 * @param notice - The Honeybadger notice being evaluated in `beforeNotify`.
 * @returns true when the class or message names a storage restriction.
 */
export function isStorageBlocked(notice: HoneybadgerNotice): boolean {
  const haystack = `${notice.name ?? ''} ${notice.message ?? ''}`
  return STORAGE_MARKERS.some((marker) => haystack.includes(marker))
}

const NOISE_CLASSIFIERS: NoiseClassifier[] = [
  { tag: THIRD_PARTY_TAG, matches: isThirdParty },
  { tag: BROWSER_EXTENSION_TAG, matches: isBrowserExtension },
  { tag: TELEMETRY_TRANSPORT_TAG, matches: isTelemetryTransport },
  { tag: STORAGE_BLOCKED_TAG, matches: isStorageBlocked }
]

/**
 * Normalizes Honeybadger's `string | string[]` tags union into a trimmed,
 * non-empty array of tags.
 * @param tags - The notice's tags in either form.
 * @returns The tags as a clean array.
 */
function normalizeTags(tags: HoneybadgerNotice['tags']): string[] {
  if (Array.isArray(tags)) {
    return tags
  }

  return (tags || '')
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean)
}

/**
 * Appends a tag to the notice's tags without duplicating it.
 * @param notice - The Honeybadger notice to tag in place.
 * @param tag - The tag to append.
 */
export function applyTag(notice: HoneybadgerNotice, tag: string): void {
  const tags = normalizeTags(notice.tags)
  if (tags.includes(tag)) return

  notice.tags = [...tags, tag]
}

/**
 * Checks whether the notice already carries a tag, in either tags form.
 * @param notice - The Honeybadger notice to inspect.
 * @param tag - The tag to look for.
 * @returns true when the tag is present.
 */
export function hasTag(notice: HoneybadgerNotice, tag: string): boolean {
  return normalizeTags(notice.tags).includes(tag)
}

/**
 * Appends {@link THIRD_PARTY_TAG} to the notice's tags if the notice is third-party.
 * @param notice - The Honeybadger notice to tag in place.
 */
export function maybeApplyThirdPartyTag(notice: HoneybadgerNotice): void {
  if (isThirdParty(notice)) applyTag(notice, THIRD_PARTY_TAG)
}

/**
 * Runs every noise classifier against the notice. Each matching classifier
 * applies its own tag, and any match also applies {@link NOISE_TAG}.
 * @param notice - The Honeybadger notice to tag in place.
 * @returns true when at least one classifier matched.
 */
export function maybeApplyNoiseTags(notice: HoneybadgerNotice): boolean {
  let matched = false

  for (const classifier of NOISE_CLASSIFIERS) {
    if (!classifier.matches(notice)) continue

    applyTag(notice, classifier.tag)
    matched = true
  }

  if (matched) applyTag(notice, NOISE_TAG)

  return matched
}

/**
 * Groups browser-extension notices by class and message only. Without this,
 * Honeybadger groups by class, message, and location. The location of an
 * extension rejection is our own reporting frame, and its URL carries the
 * host, so the same extension error splits into one fault per domain (for
 * example my.seastreak.com and tickets.seastreak.com). Run after
 * {@link maybeApplyNoiseTags}. A fingerprint that is already set is kept.
 * @param notice - The Honeybadger notice to fingerprint in place.
 * @returns true when this call set the fingerprint.
 */
export function maybeApplyNoiseFingerprint(notice: HoneybadgerNotice): boolean {
  if (notice.fingerprint) return false
  if (!hasTag(notice, BROWSER_EXTENSION_TAG)) return false

  const [message = ''] = noticeMessages(notice)
  notice.fingerprint = `${BROWSER_EXTENSION_TAG}:${notice.name ?? 'Error'}:${message}`

  return true
}

/**
 * Noise notices are tagged and sent by default so the classifiers stay
 * auditable. Dropping is opt-in, for plan-quota pressure only.
 * @returns true when `VITE_HONEYBADGER_DROP_NOISE` is the string `true`.
 */
export function shouldDropNoise(): boolean {
  return import.meta.env.VITE_HONEYBADGER_DROP_NOISE === 'true'
}
